<template>  
  <v-card flat>
    <v-form ref="form" v-model="valid" lazy-validation>
      <v-icon class="close" @click="$emit('close')">$sl-close</v-icon>
      <h2>{{ $t("labels.employment-rate") }}</h2>
      <QualificationTimeModificationList :studentName="employee.firstName" :educationId="employee.educations[0].id" :displayButtons="false" :renderKey="renderKey"></QualificationTimeModificationList>
      <div class="mt-n2">
        <v-radio-group
          v-model="rateChangeMethod"
          :key="rateChangeMethod"
          column
        >
          <v-radio
            name="rateChangeMethod"
            :label="$t('modals.employment-rate-form.change-method.no-change')"
            :value="0"
            key="0"
          ></v-radio>
          <v-radio
            name="rateChangeMethod"
            :label="$t('modals.employment-rate-form.change-method.start-at-current-date', {currentDate: this.startDate})"
            :value="1"
            key="1"
          ></v-radio>
          <v-radio
            name="rateChangeMethod"
            :label="$t('modals.employment-rate-form.change-method.set-start-date', {currentDate: this.startDate})"
            :value="2"
            key="2"
          ></v-radio>
        </v-radio-group>
      </div>
      <div>
        <div v-show="rateChangeMethod==1 || rateChangeMethod==2">
          <v-text-field
            outlined
            v-model="employmentRate"
            :rules="[rules.required, rules.employmentRate]"
            :label="$t('labels.employment-rate')"
            @input="debouncedOnChange"
          ></v-text-field>
          <DatePicker
            :label="$t('labels.from')"
            v-model="startDate"
            :miniumDate="minDate"
            :maximumDate="maxDate"
            :placeholder="$t('application.instructor.date.date-placeholder')"
            :rules="(rateChangeMethod==1 || rateChangeMethod==2) ? [rules.required] : []"
            @input="debouncedOnChange"
          />
        </div>
        <div v-show="rateChangeMethod == 2">
          <DatePicker
            :label="$t('labels.to')"
            v-model="endDate"
            :miniumDate="startDate"
            :maximumDate="maxDate"
            :placeholder="$t('application.instructor.date.date-placeholder')"
            :rules="rateChangeMethod == 2 ? [rules.required] : []"
            @input="debouncedOnChange"
          />
        </div>
        <template v-if="rateChangeMethod==1 || rateChangeMethod==2">
          <MessageBox v-if="overlapping" class="mb-5" type="alert">
            <p>{{ $t("errors.form-validation-overlapping") }}</p>
          </MessageBox>
          <MessageBox v-else class="mb-5" type="info">
            <p>Med ovan förändring av sysselsättningsgraden slutar kvalificeringstiden {{ calculatedEndDate | date}}</p>
          </MessageBox>
        </template>
        <div>
          <ActionButton
            class="mt-4"
            :isDisabled="rateChangeMethod==0 || overlapping"          
            actionIcon="$sl-save"
            @click="save"
          >{{ $t('labels.save') }}</ActionButton>
          <ActionButton
            class="mt-4"
            type="application"
            actionIcon="$sl-cancel"
            @click="$emit('close')"
          >{{ $t('labels.cancel') }}</ActionButton>
        </div>    
      </div>
    </v-form>
  </v-card>
</template>

<script>
import API from '@/services/api.js'
import {RulesMixin} from '@/components/mixins'
import ActionButton from '@/components/Common/ActionButton'
import QualificationTimeModificationList from '@/components/Common/QualifyingTime/QualificationTimeModificationList'
import get from 'lodash/get'
import debounce from 'lodash/debounce'
import DatePicker from "@/components/Common/DatePicker"
import MessageBox from "@/components/Common/MessageBox.vue";

const SugarDate = require('sugar/date')

// @group Company
// CompanyEmployeeRate component
export default {
  name: 'CompanyEmployeeRateModal',
  mixins: [RulesMixin],
  props: {
    shown: {
      type: Boolean,
      default: false
    },
    employee: {
      type: Object,
      default: null
    },
    qualificationTimeModification: {
      type: Object,
      default: null
    }
  },

  data: () => ({
    employmentRate: "100%",
    startDate: new Date(),
    endDate: new Date(),
    currentDate: new Date(),
    rateChangeMethod: 0,
    calendar: false,
    calendarTo: false,
    calendarFrom: false,
    valid: false,
    minDate: '',
    maxDate: '',
    calculatedEndDate: new Date(),
    overlapping: true,
    renderKey: 0
  }),

  components: {
    ActionButton,
    QualificationTimeModificationList,
    DatePicker,
    MessageBox
  },

  created() {
    this.init()
  },

  computed: {
    debouncedOnChange() {
      return debounce(this.periodChanged, 500)
    }
  },

  watch: {
    /**
     * @vuese
     * Used to watch show prop and open modal
     */
    shown: {
      handler() {
        this.init();
      },
    },

    qualificationTimeModification: {
      handler() {
        this.init();
      },
      deep: true,
    },
  },

  methods: {
    /**
     * @vuese
     * Used to create a bookpause
     */

    async init() {
      this.currentDate = new Date().toISOString().substr(0, 10)
      if(this.qualificationTimeModification) {
        this.startDate = new Date(this.qualificationTimeModification.fromDate).toISOString().substr(0, 10)
        this.endDate = new Date(this.qualificationTimeModification.toDate).toISOString().substr(0, 10)
        this.employmentRate = this.qualificationTimeModification.employmentRate*100
      }
      else {
        this.startDate = new Date().toISOString().substr(0, 10)
        this.endDate = new Date().toISOString().substr(0, 10)
        this.employmentRate = 100
      }

      this.minDate = get(this, 'employee.educations[0].qualificationTime.fromDate', null)
      
      if(!this.minDate) {
        this.minDate = new Date()
      }
      else {
        this.minDate = new Date(this.minDate)
      }

      this.minDate = this.minDate.toISOString().substr(0, 10)

      await this.periodChanged()

      this.renderKey += 1
    },

    async periodChanged() {
      let newCalculatedEndDate
      try {
        const data = {
          id: get(this,'qualificationTimeModification.id', null),
          employmentRate: parseFloat(this.employmentRate || 100) / 100.0,
          startDate: this.startDate,
          endDate: this.rateChangeMethod === 2 ? this.endDate : null
        }
        newCalculatedEndDate = await API.getQualificationTimeLength(this.employee.educations[0].id, { data: data })
      } catch(e) {
        console.log('e', e)
        //- Nada
      }
      if (newCalculatedEndDate) {
        const newEndDate = get(newCalculatedEndDate, 'data.endDate', null)
        if (newEndDate) {
          this.calculatedEndDate = new Date(newEndDate)
          this.maxDate = this.calculatedEndDate.toISOString().substr(0, 10)
        } else {
          this.calculatedEndDate = null
          this.maxDate = ''
        }
        this.overlapping = get(newCalculatedEndDate,'data.error', null) ? true : false
      }
    },

    convertToPercentage(rate) {
      return (rate * 100) + "%"
    },

    /**
     * @vuese
     * Used to cancel modal
     */
    cancel() {
      //used to close the modal
      this.showDialog = false
      // Fire close modal
      // @arg The argument is a string
      this.$emit('close')
    },

    async save() {
      if (!this.$refs.form.validate()) {
        this.$events.fire("showError", this.$t("errors.form-validation"));
        return;
      }

      await this.periodChanged()

      if (this.overlapping) {
        this.$events.fire("showError", this.$t("errors.form-validation-overlapping"));
        return
      }
      if (new Date(this.startDate) < new Date(this.minDate)) {
        this.$events.fire("showError", this.$t("errors.form-validation-min-date"));
        return
      }

      if (new Date(this.endDate) > new Date(this.maxDate)) {
        this.$events.fire("showError", this.$t("errors.form-validation-max-date"));
        return
      }
      if (new Date(this.endDate) < new Date(this.startDate)) {
        this.$events.fire("showError", this.$t("errors.form-validation-start-end-date"));
        return
      }

      let data = {
        employmentRate: parseFloat(this.employmentRate || 100) / 100.0,
        reason: 'Ändring av sysselsättningsgrad',
        educationId: this.employee.educations[0].id,
        startDate: this.startDate,
        endDate: this.rateChangeMethod === 2 ? this.endDate : null
      }
      const promise = Boolean(this.qualificationTimeModification) ? API.updatePauseBook(this.qualificationTimeModification.id, { data: data }) : API.createPauseBook({ data: data })
      
      promise.then(response => {
        this.$events.fire('showInfo', {message: this.$t('success.saved'), timeout: 4000})
        this.$nextTick(() => {
          this.$router.push({name: 'CompanyApprentices', params: {id: this.$route.params.id}})
        })
      })
      .catch(error => {
        this.$events.fire('showError', this.$t('errors.st-ktid-ssgr-error'))
        console.log('There was an error:', error)
      })
    }
  }
}
</script>

